import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, User, ArrowLeft, ArrowRight, TrendUp, Envelope, Heartbeat } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { fireCompleteRegistration } from "../components/TrackingPixels";

const Register = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  // Capture referral code and UTM source from URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      toast.success(`Código de indicação aplicado: ${refCode.toUpperCase()}`);
    }
    
    // Capture UTM parameters for ad tracking
    const utm = searchParams.get('utm_source') || searchParams.get('source') || searchParams.get('fbclid') ? 'facebook' : searchParams.get('ttclid') ? 'tiktok' : '';
    if (utm) {
      setUtmSource(utm);
    }
    // Also check for Facebook/TikTok click IDs
    if (searchParams.get('fbclid')) {
      setUtmSource('facebook');
    } else if (searchParams.get('ttclid')) {
      setUtmSource('tiktok');
    } else if (searchParams.get('gclid')) {
      setUtmSource('google');
    }
  }, [searchParams]);

  const formatPhone = useCallback((value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 9) {
      return numbers;
    }
    return numbers.slice(0, 9);
  }, []);

  const handlePhoneChange = useCallback((e) => {
    setPhone(formatPhone(e.target.value));
  }, [formatPhone]);

  const validateEmail = useCallback((email) => {
    if (!email) return false; // Email is now required
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const handleNext = useCallback(() => {
    if (step === 1) {
      if (!name.trim()) {
        toast.error("Por favor, insere o teu nome");
        return;
      }
      if (!email.trim()) {
        toast.error("Por favor, insere o teu e-mail");
        return;
      }
      if (!validateEmail(email)) {
        toast.error("Por favor, insere um e-mail válido");
        return;
      }
      if (phone.length !== 9) {
        toast.error("Por favor, insere um número válido (9 dígitos)");
        return;
      }
      setStep(2);
    }
  }, [step, name, phone, email, validateEmail]);

  const handlePinChange = useCallback((index, value) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    
    setPin(prevPin => {
      const newPin = [...prevPin];
      newPin[index] = digit;
      return newPin;
    });

    // Auto-focus next input
    if (digit && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }, []);

  const handlePinKeyDown = useCallback((index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  }, [pin]);

  const pinValue = useMemo(() => pin.join(''), [pin]);

  const handleSubmit = useCallback(async () => {
    if (pinValue.length !== 4) {
      toast.error("Por favor, cria um PIN de 4 dígitos");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name,
        email: email || undefined,
        phone: `+244${phone}`,
        pin: pinValue
      };
      
      // Include referral code if present
      if (referralCode) {
        payload.referred_by = referralCode;
      }
      
      // Include UTM source for ad tracking
      if (utmSource) {
        payload.utm_source = utmSource;
      }
      
      const response = await axios.post(`${API}/auth/register`, payload);

      // Fire conversion events for all pixels
      fireCompleteRegistration({
        phone: `+244${phone}`,
        referral_code: referralCode || undefined
      });

      login(response.data.token, response.data.user);
      toast.success("Bem-vindo(a) ao Nzadi Fit!");
      navigate("/focus");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  }, [pinValue, name, email, phone, referralCode, utmSource, login, navigate]);

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    }
  }, [step]);

  // Memoized PIN inputs to prevent unnecessary re-renders
  const pinInputs = useMemo(() => (
    <div className="flex justify-center gap-3 mb-8">
      {[0, 1, 2, 3].map((index) => (
        <input
          key={`pin-input-${index}`}
          id={`pin-${index}`}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={pin[index]}
          onChange={(e) => handlePinChange(index, e.target.value)}
          onKeyDown={(e) => handlePinKeyDown(index, e)}
          className="w-14 h-14 text-2xl text-center bg-accent text-white border-2 border-primary/30 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          data-testid={`pin-slot-${index}`}
          autoComplete="off"
        />
      ))}
    </div>
  ), [pin, handlePinChange, handlePinKeyDown]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        {step > 1 && (
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
        )}
        <h1 className="text-xl font-bold">Nzadi Fit</h1>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <AnimatePresence mode="wait" initial={false}>
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-md text-center"
            >
              {/* Brand with Health Icon */}
              <div className="mb-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heartbeat size={48} className="text-primary" weight="duotone" />
                </div>
                <h1 className="text-4xl font-bold text-primary mb-2">Nzadi Fit</h1>
                <p className="text-muted-foreground mt-2">Construindo o corpo dos teus sonhos</p>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={24} />
                  <input
                    type="text"
                    placeholder="Seu Nome"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-accent text-white placeholder:text-white/70 border-2 border-transparent focus:border-primary rounded-2xl pl-12 pr-6 py-4 text-lg outline-none transition-all"
                    data-testid="name-input"
                  />
                </div>

                <div className="relative">
                  <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={24} />
                  <input
                    type="email"
                    placeholder="E-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-accent text-white placeholder:text-white/70 border-2 border-transparent focus:border-primary rounded-2xl pl-12 pr-6 py-4 text-lg outline-none transition-all"
                    data-testid="email-input"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zest text-xs">*</span>
                </div>

                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={24} />
                  <span className="absolute left-12 top-1/2 -translate-y-1/2 text-white/90 font-medium text-sm">+244</span>
                  <input
                    type="tel"
                    placeholder="9xx xxx xxx"
                    value={phone}
                    onChange={handlePhoneChange}
                    className="w-full bg-accent text-white placeholder:text-white/70 border-2 border-transparent focus:border-primary rounded-2xl pl-[5.5rem] pr-6 py-4 text-lg outline-none transition-all"
                    maxLength={9}
                    data-testid="phone-input"
                  />
                </div>

                {referralCode && (
                  <div className="bg-primary/20 border border-primary/30 rounded-xl p-3 text-center">
                    <p className="text-sm text-primary font-medium">
                      🎁 Código de indicação: <span className="font-bold">{referralCode}</span>
                    </p>
                  </div>
                )}

                <button
                  onClick={handleNext}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="continue-button"
                >
                  Começar
                  <ArrowRight size={20} weight="bold" />
                </button>
              </div>

              <p className="mt-6 text-muted-foreground">
                Já tens conta?{" "}
                <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="login-link">
                  Entrar
                </Link>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-md text-center"
            >
              {/* PIN Icon */}
              <div className="mb-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendUp size={48} className="text-primary" weight="duotone" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Cria a tua Chave</h2>
                <p className="text-muted-foreground mt-2">Uma palavra-passe simples para o teu diário.</p>
              </div>

              {/* PIN Input - Custom implementation to avoid removeChild issues */}
              {pinInputs}

              <button
                onClick={handleSubmit}
                disabled={loading || pinValue.length !== 4}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-button"
              >
                {loading ? "A criar..." : "Confirmar"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-muted-foreground text-sm">
        Nzadi Fit © 2026 – Saúde em Angola 🇦🇴
      </footer>
    </div>
  );
};

export default Register;
